import classNames from "classnames";
import { useHistory, useLocation } from "react-router-dom";
import { t } from "@/i18n-js/instance";
import { useProfileDrawer } from "@/react/components/Drawers/ProfileDrawer";
import { UserImageWithFallback } from "@/react/components/shared/uikit/UserImageWithFallback";
import { useElectronAPI } from "@/react/hooks/utils/useElectronAPI";
import { usePunditUserContext } from "@circle-react/contexts";
import {
  memberProfileModalPathsWithState,
  signOutPath,
} from "@circle-react/helpers/urlHelpers";
import type { Option } from "@circle-react-shared/uikit/Dropdown/Option";
import { Dropdown } from "@circle-react-uikit/Dropdown";
import { POPPER_OPTIONS } from "./constants";
import { userProfileIconsV3 } from "./useUserProfileIcons";

export interface ProfileSectionDropdownProps {
  isPreviewMode?: boolean;
}

export const ProfileSectionDropdown = ({
  isPreviewMode = false,
}: ProfileSectionDropdownProps) => {
  const { isElectron, electronAPI } = useElectronAPI();
  const location = useLocation();
  const history = useHistory();

  const { currentCommunityMember, isViewOnlyMasquerading } =
    usePunditUserContext();
  const { show: showMemberProfileDrawer } = useProfileDrawer();

  const {
    authenticationIcon,
    billingIcon,
    paywallsAffiliatesIcon,
    viewProfileIcon,
    editProfileIcon,
    notificationIcon,
    signOutIcon,
  } = userProfileIconsV3;

  const {
    can_access_billing: shouldShowMemberBilling,
    can_access_authentication_page: canAccessAuthenticationPage,
    can_show_paywalls_affiliate: canShowPaywallsAffiliate,
  } = currentCommunityMember?.policies || {};

  const handleSignOut = () => {
    try {
      if (isElectron) {
        electronAPI?.logout?.();
      }
    } finally {
      window.location.href = signOutPath();
    }
  };

  const onProfileViewClick = () =>
    history.push(memberProfileModalPathsWithState.profile(location));

  const getMemberAuthenticationRoutes = (): Option[] => {
    if (!canAccessAuthenticationPage) return [];

    return [
      {
        value: t("authentication"),
        label: t("authentication"),
        pathTo: memberProfileModalPathsWithState.authentication(location),
        icon: authenticationIcon,
        iconSize: 20,
        dataTestId: "authentication-link",
      },
    ];
  };

  const getMemberBillingRoutes = (): Option[] => {
    if (!shouldShowMemberBilling) return [];

    return [
      {
        value: t("billing"),
        label: t("billing"),
        pathTo: memberProfileModalPathsWithState.billing(location),
        icon: billingIcon,
        iconSize: 20,
        dataTestId: "member-billing-link",
      },
    ];
  };

  const getMemberAffiliateRoutes = (): Option[] => {
    if (!canShowPaywallsAffiliate) return [];

    return [
      {
        value: t("member_profile_modal.affiliate_dashboard.title"),
        label: t("member_profile_modal.affiliate_dashboard.title"),
        pathTo: memberProfileModalPathsWithState.affiliate(location),
        icon: paywallsAffiliatesIcon,
        iconSize: 20,
        dataTestId: "member-affiliate-link",
      },
    ];
  };

  const getMemberDefaultRoutes = (): Option[] => [
    {
      value: t("view_profile"),
      label: t("view_profile"),
      onClick: () =>
        showMemberProfileDrawer({
          memberPublicId: currentCommunityMember?.public_uid ?? "",
        }),
      icon: viewProfileIcon,
      iconSize: 20,
      dataTestId: "profile-link",
    },
    {
      value: t("edit_profile"),
      label: t("edit_profile"),
      onClick: onProfileViewClick,
      icon: editProfileIcon,
      iconSize: 20,
      dataTestId: "edit-profile-link",
    },
    {
      value: t("notification"),
      label: t("notification"),
      pathTo: memberProfileModalPathsWithState.notifications(location),
      icon: notificationIcon,
      iconSize: 20,
      dataTestId: "notification-link",
    },
  ];
  const getMemberSignOutRoutes = (): Option[] => {
    const routes: Option[] = [
      {
        value: "separator",
      },
      {
        value: t("sign_out"),
        label: t("sign_out"),
        onClick: handleSignOut,
        icon: signOutIcon,
        iconSize: 20,
        dataTestId: "sign-out-link",
      },
    ];

    return routes;
  };

  const USER_PROFILE_DROPDOWN_OPTIONS: Option[] = [
    ...getMemberDefaultRoutes(),
    ...getMemberAuthenticationRoutes(),
    ...getMemberBillingRoutes(),
    ...getMemberAffiliateRoutes(),
    ...getMemberSignOutRoutes(),
  ];

  if (!currentCommunityMember) return null;

  const { avatar_url: src, name } = currentCommunityMember || {};

  return (
    <Dropdown
      direction="top-start"
      className={classNames({
        "pointer-events-none": isPreviewMode || isViewOnlyMasquerading,
      })}
      buttonLabel={t("user_menu_options")}
      button={
        <UserImageWithFallback name={name} src={src} size="8" isFullRounded />
      }
      buttonClassName="focus-visible:outline-secondary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 !rounded-full"
      buttonWrapperClassName="flex items-center"
      popperOptions={POPPER_OPTIONS}
      transitionWrapperClassName="z-30"
      menuWrapperClassName="!mt-2 !px-0"
      dataTestId="user-profile"
    >
      <div data-testid="user-profile-menu">
        <Dropdown.IconWithBadgeList options={USER_PROFILE_DROPDOWN_OPTIONS} />
      </div>
    </Dropdown>
  );
};
